import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
// TODO: switch to vertical view - https://github.com/daniel-hauser/react-organizational-chart/pull/66
import { Tree, TreeNode } from 'react-organizational-chart';

import BeenhereIcon from '@mui/icons-material/Beenhere';
import EditIcon from '@mui/icons-material/Edit';
import classNames from 'classnames';
import { AccountContext } from '../context/account';
import { OrganisationContext } from '../context/organisation';
import { OrganisationBase, OrganisationDetails } from '../core/global';
import styles from "../styles/components/organisation-diagram.module.scss";

import { Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import plur from 'plur';
import { displayUserRole, findOrganisation } from '../helpers/core';
import Loader from './loader';
import { OrganisationSettingsModal } from './organisation-settings-modal';

type Org = (number | string | null)[] | null

interface BlockProps {
  children: React.ReactNode
  userOrganisation: null | OrganisationDetails[] | undefined
  id: number | null | string
  onEdit: (id: number | string | null) => void
}

interface RoleProps {
  userOrganisation: null | OrganisationDetails[] | undefined
  id: number | null | string
}

interface OrganisationMapItem extends OrganisationBase {
  children: OrganisationMapItem[]
}


interface OrganisationMap {
  [key: number | string]: OrganisationMapItem
}

const transformOrganisationObject = (organisations: OrganisationBase[][] ) => {
  const result: OrganisationMapItem[][] = []
  organisations.forEach((organisation: OrganisationBase[]) => {
    const organisationMap: OrganisationMap = {};
    
    organisation.forEach((org: OrganisationBase) => {
      organisationMap[org.id] = { ...org, children: [] }; // Initialize with children array
    });

    organisation.forEach((org: OrganisationBase) => {
      const ownerId = org.owner_id;
      if (ownerId 
            && organisationMap[ownerId] 
            && org.type !== 'group' 
            && ownerId !== org.id
      ) {
        organisationMap[ownerId].children.push(organisationMap[org.id]);
      }
    });

    const res = Object.values(organisationMap).filter((org: OrganisationBase) => org.owner_id === org.id || (org.owner_id!== null && !organisationMap[org.owner_id]));
    result.push(res)
  });

	return result
}

const Role = ({
  userOrganisation,
  id
}: RoleProps) => {
  const currentOrganisation = findOrganisation(userOrganisation, id)
  if (!currentOrganisation) {
    return null
  }

  return (
    <span className={styles.role}>Your role: <strong>{displayUserRole(currentOrganisation.role)}</strong></span>
  )
}

const Block = ({
  children,
  id,
  userOrganisation,
  onEdit,
}: BlockProps) => {
  const found = findOrganisation(userOrganisation, id)
  const isMember = typeof found !== 'undefined'
  const canEdit = isMember && (found.role === 'admin' || found.role === 'super-admin')
  return (
    <div className={classNames(styles.block, {
      [styles.isMemberOf]: isMember
    })}>
      {children}
      {isMember && canEdit &&
        <Button
          className={styles.editButton}
          onClick={()=> {
            if (onEdit) {
              onEdit(id)
            }
          }
        }>
          <EditIcon titleAccess='Edit your organisation' />
          <BeenhereIcon titleAccess='This is your organisation' />
        </Button>}
      {isMember && <Role userOrganisation={userOrganisation} id={id} />}
    </div>
  )
}

interface TreeStructureProps {
  data: OrganisationMapItem[]
  organisation: OrganisationDetails[] | null | undefined
  onEdit: (id: number | string | null) => void
}

const TreeStructure = ({
  data,
  organisation,
  onEdit,
}: TreeStructureProps) => {
  return (
    <div className={styles.treeWrapper}>
      <Tree
        lineWidth={'2px'}
        lineColor={'green'}
        lineBorderRadius={'8px'}
        label={<Block id={data[0].id} userOrganisation={organisation} onEdit={onEdit}>{data[0].name}</Block>}
      >
        <>
          {data[0].children.map((child: OrganisationMapItem) => {
            return (
              <TreeBox key={child.id} userOrganisation={organisation} organisation={child} onEdit={onEdit} />
            )
          })
        }
        </>
      </Tree>
    </div>
  )
}

interface BoxProps {
  organisation: OrganisationMapItem
  userOrganisation: null | OrganisationDetails[] | undefined
  onEdit: (id: number | string | null) => void
}

const TreeBox = ({ organisation, userOrganisation, onEdit }: BoxProps) => {
  return (
    <TreeNode 
      label={<Block 
              userOrganisation={userOrganisation} 
              id={organisation.id} 
              onEdit={()=> {
                  if (onEdit) {
                    onEdit(organisation.id)
                  }
                }
              }
            >{organisation.name}</Block>}
      >
      {organisation.children && organisation.children.length > 0 && (
        <>
          {organisation.children.map((child: OrganisationMapItem) => (
            <TreeBox 
              key={child.id} 
              onEdit={()=> {
                  if (onEdit) {
                    onEdit(child.id)
                  }
                }
              } 
              userOrganisation={userOrganisation} 
              organisation={child} 
            />
          ))}
        </>
      )}
    </TreeNode>
  );
};


export const OrganisationDiagram = ({}) => {
  const [currentOrganisationId, setCurrentOrganisationId] = useState<number | string | null>(null)

  const [showCompanySettings, setShowCompanySettings] = useState(false)

  const { organisations, loading, refetchOrganisations  } = useContext(OrganisationContext);
  const { account, refetchUserDetails  } = useContext(AccountContext);

  // organisation_member table defines the relation ship among organisations
  
  // const userOrganisation: Org = useMemo(() => {
  //   if (account) {
  //     const {organisation} = account
  //     if (organisation) {
  //       return organisation.map((o: OrganisationBase) => o.id)
  //     }
  //   }
  //   return null
  // }, [account])

  const orderedData: OrganisationMapItem[][] | null = useMemo(() => {
    if (organisations !== null && Array.isArray(organisations)) {
      if (organisations.length === 0) {
        return []
      }

      return transformOrganisationObject([...organisations])
    }
    return null
  }, [organisations])

  const useAccountOrganisation = useMemo(() => {
    const {organisation} = account
    if (organisation) {
      return organisation.filter( org => org.role === 'admin' || org.role === 'super-admin')
    }
    return []
  }, [])
  // useEffect(() => {
  //   // if (organisations === null) {
  //     refetchOrganisations()
  //     refetchUserDetails()
  //   // }
  // }, []);

  const setOrganisationForEdit = useCallback((orgId: string | number | null) => {
    setCurrentOrganisationId(orgId);
    if (orgId !== null) {
      setShowCompanySettings(true)
    }
  }, [])

  useEffect(() => {
    if (showCompanySettings === false) {
      refetchOrganisations()
      refetchUserDetails()
    }
  }, [showCompanySettings]);
  

  if (orderedData === null) {
    return (
      <div className={styles.wrapper}>
        {loading && <Loader center />}
      </div>
    );
  }
  return (
    <div className={styles.wrapper}>
        {orderedData.map((data: OrganisationMapItem[]) => {
          return <TreeStructure 
                  data={data} 
                  organisation={account?.organisation} 
                  onEdit={(id: number | string | null) => {
                    setOrganisationForEdit(id)
                  }}
                />
        })}
        {
          orderedData.length === 0 &&
            <>
              { (account && account.organisation) ?
                <>
                  <p>You are member of {account.organisation.length} {plur('organisation', account.organisation.length)}.</p>
                  {account.organisation.map((org: any) => {
                    const {id, name} = org
                    return <div className={styles.blockWrapper}>
                      <Block id={id} userOrganisation={account?.organisation} onEdit={(id: number | string | null) => {
                        setOrganisationForEdit(id);
                      }}>{name}</Block>
                    </div>
                  })}
                </>
                :
                <>
                  <p>Your account is a stand-alone account. There is no organisation associated with your account. To share content with other users you must first create a company or be added to account by administrator.</p>
                  <Button
                    variant="contained"
                    onClick={() =>{
                      setShowCompanySettings(true)
                    }}
                    >Create a company</Button>
                </>
              }
            </>
        }
        {useAccountOrganisation.length > 0 && 
          <FormControl fullWidth>
            <InputLabel id="org-select">Edit your organisation</InputLabel>
            <Select
              labelId="org-select"
              id="org-select"
              value={`${currentOrganisationId}`}
              label="Select your organisation"
              onChange={(event: SelectChangeEvent) => {
                  setOrganisationForEdit(event.target.value as string);
                }
              }
            >
              {
                useAccountOrganisation.map((org: OrganisationDetails) => {
                  const {id, name, role} = org
                  return (
                    <MenuItem 
                    key={`${id}-${name}`} 
                    value={id} 
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setOrganisationForEdit(id);
                    }
                  }>{name}</MenuItem>
                  )
                })
              }
              {/* <MenuItem value={0}>Create new</MenuItem> */}
            </Select>
          </FormControl>
        }
      {showCompanySettings &&
        <OrganisationSettingsModal 
          id={currentOrganisationId}
          handleClose={() => {
            setShowCompanySettings(false)
          }}
        />
      }
    </div>
  );
}